import { useState, type FormEvent } from "react";
import PageLayout from "@/components/feature/PageLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import AnimatedSection from "@/components/AnimatedSection";
import { blogArticles, blogCategories } from "@/mocks/blogData";

const NEWSLETTER_FORM_URL = "https://readdy.ai/api/form/da39in90938upkd4h6v0";

function BlogCard({ article, index }: { article: (typeof blogArticles)[number]; index: number }) {
  return (
    <AnimatedSection delay={(index % 3) * 100}>
      <a
        href={`/blog/${article.slug}`}
        className="group flex flex-col rounded-2xl overflow-hidden bg-background-100 border border-background-200/60 hover:border-accent-300/40 hover:shadow-xl hover:shadow-accent-500/10 transition-all duration-500 card-lift h-full"
      >
        <div className="relative overflow-hidden h-[210px]">
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
          />
          <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-background-50/90 backdrop-blur text-xs font-bold text-accent-600 border border-accent-200/50">
            {article.category}
          </span>
        </div>
        <div className="flex flex-col p-6 flex-grow">
          <div className="flex items-center gap-3 text-xs text-foreground-500 mb-3">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line text-accent-500" />
              {article.author}
            </span>
            <span className="w-1 h-1 rounded-full bg-foreground-300" />
            <span>{article.date}</span>
          </div>
          <h3 className="text-base md:text-lg font-bold text-foreground-950 mb-2.5 font-heading leading-snug group-hover:text-accent-600 transition-colors">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-600 leading-relaxed mb-5 flex-grow">
            {article.description}
          </p>
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent-600 btn-arrow-hover group/link">
            Read More
            <i className="ri-arrow-right-line btn-arrow text-xs" />
          </span>
        </div>
      </a>
    </AnimatedSection>
  );
}

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [email, setEmail] = useState("");
  const [formState, setFormState] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const featured = blogArticles.find((a) => a.featured);
  const latest = blogArticles.slice(0, 6);
  const popular = blogArticles.filter((a) => a.popular);

  const filtered =
    activeCategory === "All"
      ? blogArticles
      : blogArticles.filter((a) => a.category === activeCategory);

  const handleSubscribe = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const honeypot = data.get("contact_alt");
    if (honeypot && String(honeypot).trim() !== "") {
      setFormState("success");
      form.reset();
      return;
    }
    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key !== "contact_alt" && value) payload.append(key, String(value));
    });
    setFormState("submitting");
    setFormError("");
    try {
      const res = await fetch(NEWSLETTER_FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText;
      const isSpam = String(serverMsg).toLowerCase().includes("spam");
      if (!res.ok || parsed?.code !== "OK" || isSpam) {
        setFormState("error");
        setFormError(serverMsg && String(serverMsg) !== "" ? String(serverMsg) : "Something went wrong. Please try again.");
        return;
      }
      setFormState("success");
      form.reset();
    } catch {
      setFormState("error");
      setFormError("Something went wrong. Please try again.");
    }
  };

  return (
    <PageLayout>
      <Breadcrumb items={[{ label: "Home", href: "/" }, { label: "Blog" }]} />

        {/* Hero */}
        <section className="relative py-20 md:py-24 bg-hero-gradient overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")" }} />
          <div className="container-astro relative z-10 text-center">
            <AnimatedSection>
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-accent-500/10 border border-accent-500/20 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-400 animate-pulse-dot" />
                <span className="text-xs font-semibold text-accent-300 tracking-wide uppercase">AstroJal Blog</span>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={100}>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[52px] font-extrabold text-background-50 leading-[1.1] mb-6 font-heading tracking-tight">
                Insights, Ideas &amp; Technology
              </h1>
            </AnimatedSection>
            <AnimatedSection delay={200}>
              <p className="text-base md:text-lg text-background-400 leading-relaxed mb-8 max-w-2xl mx-auto">
                Practical insights about Laravel, software development, APIs, SaaS, AI, cloud and modern technology.
              </p>
            </AnimatedSection>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background-50 to-transparent pointer-events-none" />
        </section>

        {/* Category filters */}
        <section className="py-10 md:py-12 bg-background-50 border-b border-background-200/50">
          <div className="container-astro">
            <div className="flex flex-wrap items-center justify-center gap-2.5">
              {["All", ...blogCategories].map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap ${
                    activeCategory === cat
                      ? "bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#F97316] text-white shadow-lg shadow-[#7C3AED]/20"
                      : "bg-background-100 text-foreground-600 border border-background-200/60 hover:border-accent-300/40 hover:text-accent-600"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Article */}
        {featured && activeCategory === "All" && (
          <section className="py-16 md:py-20 bg-background-100">
            <div className="container-astro">
              <AnimatedSection>
                <div className="text-center mb-10">
                  <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-foreground-950 mb-3 font-heading tracking-tight">
                    Featured Article
                  </h2>
                  <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
                </div>
              </AnimatedSection>
              <AnimatedSection delay={100}>
                <a
                  href={`/blog/${featured.slug}`}
                  className="group grid grid-cols-1 lg:grid-cols-2 rounded-2xl overflow-hidden bg-background-50 border border-background-200/60 hover:border-accent-300/40 hover:shadow-xl hover:shadow-accent-500/10 transition-all duration-500"
                >
                  <div className="relative overflow-hidden h-[260px] lg:h-auto">
                    <img
                      src={featured.image}
                      alt={featured.title}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex flex-col justify-center p-8 md:p-10">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="px-3 py-1 rounded-full bg-accent-500/10 text-accent-600 text-xs font-bold border border-accent-200/50">
                        {featured.category}
                      </span>
                      <span className="text-xs text-foreground-500">{featured.readingTime}</span>
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3 font-heading leading-snug group-hover:text-accent-600 transition-colors">
                      {featured.title}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                      {featured.description}
                    </p>
                    <div className="flex items-center gap-3 text-sm text-foreground-500 mb-6">
                      <span className="w-9 h-9 flex items-center justify-center rounded-full bg-gradient-to-r from-[#2563EB] to-[#7C3AED] text-white text-xs font-bold">
                        AE
                      </span>
                      <div className="flex flex-col">
                        <span className="font-semibold text-foreground-800">{featured.author}</span>
                        <span className="text-xs">{featured.date}</span>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent-600 btn-arrow-hover group/link">
                      Read Article
                      <i className="ri-arrow-right-line btn-arrow" />
                    </span>
                  </div>
                </a>
              </AnimatedSection>
            </div>
          </section>
        )}

        {/* Latest Articles */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="container-astro">
            <AnimatedSection>
              <div className="text-center mb-10">
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-foreground-950 mb-3 font-heading tracking-tight">
                  {activeCategory === "All" ? "Latest Articles" : activeCategory}
                </h2>
                <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
              </div>
            </AnimatedSection>

            {filtered.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {filtered.map((article, i) => (
                  <BlogCard key={article.slug} article={article} index={i} />
                ))}
              </div>
            ) : (
              <p className="text-center text-foreground-500 text-sm py-10">
                No articles in this category yet.
              </p>
            )}
          </div>
        </section>

        {/* Popular Articles */}
        {activeCategory === "All" && (
          <section className="py-16 md:py-20 bg-background-100">
            <div className="container-astro">
              <AnimatedSection>
                <div className="text-center mb-10">
                  <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-foreground-950 mb-3 font-heading tracking-tight">
                    Popular Articles
                  </h2>
                  <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
                </div>
              </AnimatedSection>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {popular.map((article, i) => (
                  <BlogCard key={article.slug} article={article} index={i} />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Newsletter */}
        <section className="py-16 md:py-20 bg-hero-gradient relative overflow-hidden">
          <div className="container-astro relative z-10 max-w-3xl text-center">
            <AnimatedSection>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-background-50 mb-4 font-heading tracking-tight">
                Stay Ahead of Technology
              </h2>
              <p className="text-base text-background-400 mb-8 leading-relaxed">
                Get practical insights about Laravel, SaaS, AI and modern software development.
              </p>
            </AnimatedSection>
            <AnimatedSection delay={150}>
              {formState === "success" ? (
                <div className="inline-flex items-center gap-3 px-6 py-4 rounded-xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-sm font-semibold">
                  <i className="ri-checkbox-circle-fill text-lg" />
                  Thanks for subscribing! We'll be in touch.
                </div>
              ) : (
                <form
                  data-readdy-form
                  onSubmit={handleSubscribe}
                  className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto"
                >
                  <input
                    type="email"
                    name="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    aria-label="Email address"
                    className="flex-1 px-5 py-3.5 rounded-xl bg-background-50/10 border border-background-500/30 text-background-100 text-sm placeholder:text-background-500 focus:outline-none focus:ring-2 focus:ring-accent-500/50 focus:border-accent-500/40 transition-all"
                  />
                  <input
                    type="text"
                    name="contact_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="hp-trap"
                  />
                  <button
                    type="submit"
                    disabled={formState === "submitting"}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-gradient-to-r from-[#2563EB] via-[#7C3AED] to-[#F97316] text-white text-sm font-semibold rounded-xl hover:brightness-110 hover:-translate-y-0.5 transition-all duration-300 shadow-lg shadow-[#7C3AED]/25 whitespace-nowrap"
                  >
                    {formState === "submitting" ? "Subscribing..." : "Subscribe"}
                    <i className="ri-send-plane-line text-xs" />
                  </button>
                </form>
              )}
              {formState === "error" && (
                <p className="mt-4 text-sm text-rose-300">{formError}</p>
              )}
            </AnimatedSection>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background-50 to-transparent pointer-events-none" />
        </section>
    </PageLayout>
  );
}